/**
 * Founder 2026-10-29 (netalinissim.com) — the ONE reading of an
 * `InstallCoverage` payload into what the merchant sees: one row per
 * layer with a state, a short status word and a one-line description,
 * a summary line, and the headline the completion screen uses.
 *
 * Rules (merged UX / design / content / product review):
 *   - A verified snippet is an installed embed, not a gap: the people
 *     row is a check that waits for its first page view.
 *   - Amber only when the merchant must act. "Not connected" and
 *     "not possible with this install" are neutral.
 *   - Shopify's own files are never counted as Sill's, and the
 *     order-scoped Shopify app is never pushed on an observe-only store.
 *   - Never claim coverage the install does not have.
 */

import type { InstallCoverage } from '@sill/types/api';

import { formatRelativeTime } from '../../lib/format.js';
import { LAYER_LABEL } from '../onboarding/install-copy.js';

/**
 * live        covered and traffic seen (lime check, LIVE)
 * installed   installed and confirmed, no traffic yet (lime check, WAITING)
 * checking    a probe has no verdict yet: slow or not checked (sky, no check)
 * not_covered this install cannot see it; neutral (hollow box)
 * optional    the merchant may add it; neutral (dash)
 * action      the merchant must act; the only amber state
 */
export type CoverageRowState = 'live' | 'installed' | 'checking' | 'not_covered' | 'optional' | 'action';

export interface CoverageRow {
  readonly id: 'browser-visitors' | 'server-side-agents' | 'discovery-files' | 'store';
  readonly label: string;
  /** Short label for the summary strip. */
  readonly short: string;
  readonly state: CoverageRowState;
  /** Status word shown as a chip. */
  readonly chip: string;
  readonly description: string;
  /** One quiet docs link (never a Connect button). */
  readonly link?: { readonly label: string; readonly href: string } | undefined;
}

/**
 * Founder 2026-10-29 round 2 — the store row explains what connecting
 * adds, without asking. Pinned against the docs tree by
 * `store-docs-path.test.ts`.
 */
export const STORE_DOCS_PATH: Record<'shopify' | 'woocommerce', string> = {
  shopify: '/install/shopify/',
  woocommerce: '/install/wordpress/#connect-woocommerce',
};
const DOCS_ORIGIN = 'https://docs.sill.so';

export interface CoverageView {
  readonly rows: readonly CoverageRow[];
  /** "2 of 3 covered · store optional". */
  readonly summary: string;
  /** True when a row is waiting on a signal a re-check can resolve. */
  readonly waiting: boolean;
}

const COVERED: ReadonlySet<CoverageRowState> = new Set(['live', 'installed']);

export const CHIP: Record<CoverageRowState, string> = {
  live: 'LIVE',
  installed: 'WAITING',
  checking: 'CHECKING',
  not_covered: 'NOT COVERED',
  optional: 'OPTIONAL',
  action: 'ACTION',
};

function peopleRow(c: InstallCoverage): CoverageRow {
  const base = { id: 'browser-visitors' as const, label: LAYER_LABEL.browser, short: 'People' };
  const via = c.browser.installed_via ?? null;
  const added_by =
    via === 'worker' ? 'Added by the Cloudflare Worker.'
    : via === 'plugin' ? 'Added by the WordPress plugin.'
    : via === 'shopify_app' ? 'Added by the Sill app.'
    : null;
  if (c.browser.state === 'live') {
    const seen = c.browser.seen_at !== null ? `First seen ${formatRelativeTime(c.browser.seen_at)}.` : 'Page views are arriving.';
    return { ...base, state: 'live', chip: CHIP.live, description: added_by !== null ? `${seen} ${added_by}` : seen };
  }
  if (c.browser.installed === true) {
    const lead = via === 'snippet' ? 'Snippet found on your homepage.' : added_by ?? 'Installed.';
    return { ...base, state: 'installed', chip: CHIP.installed, description: `${lead} Waiting for the first page view.` };
  }
  return {
    ...base,
    state: c.action?.kind === 'paste_embed' ? 'action' : 'not_covered',
    chip: c.action?.kind === 'paste_embed' ? 'NOT FOUND' : CHIP.not_covered,
    description: 'No page view yet, and the snippet was not found on your homepage.',
  };
}

function noJsRow(c: InstallCoverage): CoverageRow {
  const base = { id: 'server-side-agents' as const, label: LAYER_LABEL.server_side, short: 'No-JS agents' };
  if (c.server_side.state === 'live') {
    const description =
      c.server_side.source === 'worker' ? 'Seen by the Cloudflare Worker.'
      : c.server_side.source === 'plugin' ? 'Seen by the WordPress plugin. Pages served from a CDN cache are not seen.'
      : 'Requests are arriving.';
    return { ...base, state: 'live', chip: CHIP.live, description };
  }
  // A Worker or plugin that reported once but not in the last 7 days
  // (johnnyvbt.myshopify.com, 2026-10-29): say when, never "LIVE".
  const stale = c.server_side.stale ?? null;
  const stale_lead = stale !== null
    ? `${stale.source === 'worker' ? 'The Cloudflare Worker' : 'The WordPress plugin'} last reported ${formatRelativeTime(stale.last_seen_at)}; nothing since.`
    : null;
  if (c.platform === 'shopify') {
    const lead = stale_lead ?? (c.commerce.connected === 'shopify' ? 'Not visible from the app.' : 'Not visible with a script tag.');
    const tail = stale_lead !== null ? 'Possible again with Cloudflare in front of your store.' : 'Possible later with Cloudflare in front of your store.';
    return { ...base, state: 'not_covered', chip: CHIP.not_covered, description: `${lead} ${tail}` };
  }
  if (stale_lead !== null) {
    return { ...base, state: 'not_covered', chip: CHIP.not_covered, description: `${stale_lead} Not visible until it reports again.` };
  }
  const kind = c.action?.kind;
  if (kind === 'deploy_worker' || kind === 'install_plugin' || kind === 'add_cloudflare') {
    const fix =
      kind === 'deploy_worker' ? 'The Cloudflare Worker adds them.'
      : kind === 'install_plugin' ? 'The WordPress plugin adds them.'
      : 'A Cloudflare Worker adds them.';
    return { ...base, state: 'action', chip: CHIP.not_covered, description: `Not visible with a script tag. ${fix}` };
  }
  return { ...base, state: 'not_covered', chip: CHIP.not_covered, description: 'Not visible with a script tag. A Cloudflare Worker or the WordPress plugin adds them.' };
}

function cardRow(c: InstallCoverage, domain: string): CoverageRow {
  const base = { id: 'discovery-files' as const, label: LAYER_LABEL.discovery, short: 'Agent card' };
  const d = c.discovery;
  if (d.agent_card === 'served') return { ...base, state: 'live', chip: CHIP.live, description: `Served on ${domain}.` };
  if (d.agent_card === 'redirect') return { ...base, state: 'live', chip: CHIP.live, description: `Redirected from ${domain} to Sill.` };
  if (c.platform === 'shopify') {
    if (d.app_proxy_card === 'served') {
      return { ...base, state: 'live', chip: CHIP.live, description: `Served at ${domain}/apps/sill/agent-card.json.` };
    }
    if (d.app_proxy_card === 'slow' || d.app_proxy_card === 'unknown') {
      return { ...base, state: 'checking', chip: CHIP.checking, description: `Checking ${domain}/apps/sill/agent-card.json.` };
    }
    return {
      ...base,
      state: 'not_covered',
      chip: 'HOSTED BY SILL',
      description: "Signed and current at edge.sill.so. Shopify doesn't allow these files on your store's domain.",
    };
  }
  if (d.agent_card === 'slow') return { ...base, state: 'checking', chip: 'SLOW', description: `${domain} took over 8 seconds to answer. Re-check later.` };
  if (d.agent_card === 'unknown') return { ...base, state: 'checking', chip: CHIP.checking, description: 'Not checked yet.' };
  return {
    ...base,
    state: c.action?.kind === 'add_redirect' ? 'action' : 'not_covered',
    chip: CHIP.not_covered,
    description: `Signed and current at edge.sill.so, not on ${domain} yet.`,
  };
}

function storeRow(c: InstallCoverage): CoverageRow | null {
  if (!c.commerce.applicable) return null;
  const base = { id: 'store' as const, label: LAYER_LABEL.store, short: 'Store' };
  if (c.commerce.connected !== null) {
    return { ...base, state: 'live', chip: CHIP.live, description: c.commerce.connected === 'shopify' ? 'Shopify connected.' : 'WooCommerce connected.' };
  }
  if (c.action?.kind === 'connect_store') {
    return { ...base, state: 'action', chip: 'NOT CONNECTED', description: "Agents can't read your catalog or orders yet." };
  }
  const store = c.platform === 'shopify' ? 'shopify' : 'woocommerce';
  return {
    ...base,
    state: 'optional',
    chip: CHIP.optional,
    description: c.platform === 'shopify'
      ? 'Not connected. Sill reads nothing from your store.'
      : 'Not connected. Sill reads nothing from your catalog or orders.',
    link: { label: 'What connecting adds', href: `${DOCS_ORIGIN}${STORE_DOCS_PATH[store]}` },
  };
}

export function coverageView(c: InstallCoverage, domain: string): CoverageView {
  const store = storeRow(c);
  const rows = [peopleRow(c), noJsRow(c), cardRow(c, domain), ...(store !== null ? [store] : [])];
  // Optional rows sit outside the count: connecting a store is a choice.
  const counted = rows.filter((r) => r.state !== 'optional');
  const covered = counted.filter((r) => COVERED.has(r.state)).length;
  const optional = rows.some((r) => r.state === 'optional');
  const summary = `${covered} of ${counted.length} covered${optional ? ' · store optional' : ''}`;
  const waiting = rows.some((r) => r.state === 'installed' || r.state === 'checking');
  return { rows, summary, waiting };
}

/**
 * The completion screen's one-line summary under "You're live", from
 * what is actually covered. Only server-side installs may say "every
 * request"; a script tag says what it really sees.
 */
export function coverageHeadline(c: InstallCoverage | null): string {
  if (c === null) return 'Sill is set up on your site.';
  if (c.server_side.state === 'live' && c.server_side.source === 'worker') {
    return 'Sill records every request to your site, with or without JavaScript.';
  }
  if (c.server_side.state === 'live' && c.server_side.source === 'plugin') {
    return 'Sill records every agent request that reaches WordPress, with or without JavaScript.';
  }
  const browser_live = c.browser.state === 'live';
  if (c.platform === 'shopify') {
    if (c.commerce.connected === 'shopify') {
      return 'Sill records people and browser agents on your store and serves your agent card from it.';
    }
    return browser_live
      ? 'Sill is recording people and browser agents on your store. Your orders and catalog are not touched.'
      : 'Sill records people and browser agents on your store, starting with the next page view. Your orders and catalog are not touched.';
  }
  return browser_live
    ? 'Sill is recording people and browser agents on your site.'
    : 'Sill records people and agents that run JavaScript, starting with the next page view.';
}
